import React, { useState, useEffect, useCallback } from "react";
import { FileText, X, Download, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeaders } from "@/lib/auth-utils";

const ContractPreviewModal = ({ isOpen, onClose, employee, contract }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadContractPreview = useCallback(async () => {
    if (!employee?.id) return;

    try {
      setLoadingPreview(true);
      setPreviewError(false);

      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract/download`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else {
        setPreviewError(true);
      }
    } catch (error) {
      console.error("Error loading contract preview:", error);
      setPreviewError(true);
    } finally {
      setLoadingPreview(false);
    }
  }, [employee?.id]);

  // Load contract for preview when modal opens
  useEffect(() => {
    if (isOpen && contract && employee?.id) {
      // Reset states and load preview
      setPreviewError(false);
      setPreviewUrl(null);
      loadContractPreview();
    }
  }, [isOpen, contract?.id, employee?.id, loadContractPreview]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreviewError(false);
      setLoadingPreview(false);
    }
  }, [isOpen]);

  const downloadContract = async () => {
    if (!contract || !employee?.id) return;

    try {
      setLoading(true);
      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract/download`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = contract.nom || "contract.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Succès",
          description: "Contrat téléchargé avec succès",
        });
      } else {
        throw new Error("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Error downloading contract:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le contrat",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!contract || !employee) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[794px] max-w-[90vw] h-[90vh] max-h-[1123px] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-semibold">
                  Contrat de {employee.prenom} {employee.nom}
                </div>
                <div className="text-sm font-normal text-muted-foreground">
                  {contract.nom} • Ajouté le{" "}
                  {new Date(contract.created_at).toLocaleDateString("fr-FR")}
                </div>
              </div>
            </div>
            {/* <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={downloadContract}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-1" />
                )}
                Télécharger
              </Button>
            </div> */}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden min-h-0">
          {loadingPreview ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Chargement du contrat...
                </h3>
                <p className="text-sm text-muted-foreground">
                  Veuillez patienter pendant que nous préparons le document.
                </p>
              </div>
            </div>
          ) : previewError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Impossible de charger le contrat
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Une erreur est survenue lors du chargement du document.
                  <br />
                  Veuillez essayer de télécharger le fichier directement.
                </p>
                <Button
                  variant="outline"
                  onClick={loadContractPreview}
                  className="mr-2"
                >
                  Réessayer
                </Button>
                <Button onClick={downloadContract} disabled={loading}>
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  Télécharger
                </Button>
              </div>
            </div>
          ) : previewUrl ? (
            <div className="h-full w-full bg-gray-50 p-4">
              <div className="h-full bg-white shadow-lg rounded-sm border">
                <iframe
                  src={previewUrl}
                  className="w-full h-full border-0 rounded-sm"
                  title={`Contrat de ${employee.prenom} ${employee.nom}`}
                  onError={() => setPreviewError(true)}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Préparation du contrat...
                </h3>
                <p className="text-sm text-muted-foreground">
                  Le document va se charger automatiquement.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContractPreviewModal;
